import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const cls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-fuchsia-500/30";

export function RegFormDialog({ open, onOpenChange, accountKey, event, existing, onSaved }) {
  const [form, setForm] = useState(null);
  const [showTpl, setShowTpl] = useState(false);
  const [saving, setSaving] = useState(false);
  const f = (k, val) => setForm((s) => ({ ...s, [k]: val }));
  const t = (k, val) => setForm((s) => ({ ...s, templates: { ...s.templates, [k]: val } }));

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        title: existing.title, description: existing.description, quota: existing.quota,
        deadline: existing.deadline || "", stand_options: (existing.stand_options || []).join(", "),
        templates: existing.templates || {},
      });
    } else {
      setForm({
        title: event ? `Anmeldung: ${event.title}` : "", description: "", quota: 20,
        deadline: event?.date || "", stand_options: "Kleiner Stand, Großer Stand",
        templates: {},
      });
    }
    setShowTpl(false);
  }, [open, existing, event]);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Bitte einen Titel angeben");
    setSaving(true);
    const payload = {
      title: form.title, description: form.description, quota: parseInt(form.quota) || 1,
      deadline: form.deadline, stand_options: form.stand_options.split(",").map((s) => s.trim()).filter(Boolean),
      templates: form.templates,
    };
    try {
      let data;
      if (existing) {
        ({ data } = await api.patch(`/reg-forms/${existing.id}`, payload));
      } else {
        ({ data } = await api.post("/reg-forms", { ...payload, account_key: accountKey, event_id: event?.id || "" }));
      }
      toast.success(existing ? "Formular aktualisiert" : "Anmeldeformular erstellt ✨");
      onSaved(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  const TPL_FIELDS = [
    ["confirm", "Eingangsbestätigung (sofort nach Anmeldung)"],
    ["approve", "Zusage-E-Mail"],
    ["reject", "Absage-E-Mail"],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">{existing ? "Formular bearbeiten" : "Anmeldeformular erstellen"}</DialogTitle>
          <DialogDescription>Öffentliches Formular mit Kontingent-Schutz und automatischen E-Mails.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <Field label="Titel">
            <input data-testid="regform-title" value={form.title} onChange={(e) => f("title", e.target.value)} className={cls} />
          </Field>
          <Field label="Beschreibung (wird öffentlich angezeigt)">
            <textarea data-testid="regform-description" rows={3} value={form.description} onChange={(e) => f("description", e.target.value)}
              placeholder="z. B. Ort, Uhrzeit, Standgebühr, Aufbau-Infos…" className={`${cls} h-auto py-2 resize-none`} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kontingent (max. Zusagen)">
              <input data-testid="regform-quota" type="number" min="1" value={form.quota} onChange={(e) => f("quota", e.target.value)} className={cls} />
            </Field>
            <Field label="Anmeldeschluss">
              <input data-testid="regform-deadline" type="date" value={form.deadline} onChange={(e) => f("deadline", e.target.value)} className={cls} />
            </Field>
          </div>
          <Field label="Standgrößen zur Auswahl (kommagetrennt, leer = keine Auswahl)">
            <input data-testid="regform-stands" value={form.stand_options} onChange={(e) => f("stand_options", e.target.value)} className={cls} />
          </Field>
          <button onClick={() => setShowTpl((s) => !s)} className="text-[13px] font-semibold text-fuchsia-700 hover:text-fuchsia-800">
            {showTpl ? "E-Mail-Vorlagen ausblenden" : "E-Mail-Vorlagen anpassen (optional)"}
          </button>
          {showTpl && (
            <div className="space-y-4 rounded-lg border border-neutral-200 bg-neutral-50 p-3.5">
              <p className="text-[12px] text-neutral-500">Platzhalter: {"{name}"}, {"{titel}"}, {"{stand_nummer}"}, {"{standwunsch}"} — leere Felder nutzen die Standard-Vorlage.</p>
              {TPL_FIELDS.map(([key, label]) => (
                <div key={key}>
                  <div className="text-[12px] font-bold uppercase tracking-wider text-neutral-500 mb-1.5">{label}</div>
                  <input value={form.templates[`${key}_subject`] || ""} onChange={(e) => t(`${key}_subject`, e.target.value)}
                    placeholder="Betreff (Standard verwenden)" className={`${cls} mb-1.5`} />
                  <textarea rows={3} value={form.templates[`${key}_body`] || ""} onChange={(e) => t(`${key}_body`, e.target.value)}
                    placeholder="Nachricht (Standard verwenden)" className={`${cls} h-auto py-2 resize-none`} />
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <button data-testid="regform-save" onClick={submit} disabled={saving}
            className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {existing ? "Speichern" : "Formular erstellen"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
